import { Component, computed, inject, input, ViewEncapsulation, ChangeDetectionStrategy } from "@angular/core";
import { CalendarData } from "../../modules/home/calendar/CalendarData";
import { ModalService } from "../../modules/modal/modal.service";
import { S25Datefilter } from "../../modules/s25-dateformat/s25.datefilter.service";
import { Item } from "../../pojo/Item";
import { StateService } from "../../services/state.service";
import { UserprefService } from "../../services/userpref.service";
import { S25Const } from "../../util/s25-const";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { CalendarDayReservations } from "./calendar.day.reservations.component";
import { S25CalendarStore } from "./calendar.store";

@Component({
    selector: "td[s25-ng-calendar-day-event]",
    standalone: true,
    imports: [CalendarDayReservations],
    host: {
        "[class.has-more]": "day().hasMore > 0",
        "[class.allows-create]": "showCreate()",
        "[class.loading-has-more]": "!day().hasMore && day().hasMore !== 0",
    },
    template: `
        <div class="CalendarDayHeader" [class.MonthName]="isMonthStart()">
            <a class="ngCpointer hide-on-mobile" (click)="fullDayModal()">
                @if (isMonthStart()) {
                    {{ day().month_name }}
                }
                {{ day().day }}
            </a>
            <a class="ngCpointer show-on-mobile" (click)="fullDayModal()"> {{ day().month_name }} {{ day().day }} </a>
        </div>
        <s25-ng-calendar-day-reservations class="reservations" [reservations]="reservations()" />
        <div class="ngTDBottom cell-controls">
            @if (showCreate()) {
                <div class="CalendarDayEventCreate" (click)="createEvent()">
                    <span class="ngZhigh MonthCalendar_EventCreate"> Create an event </span>
                </div>
            }
            @if (day().hasMore) {
                <button class="btn btn-default ngZhigh" (click)="fullDayModal()">
                    View All {{ this.day().hasMore }}
                </button>
            } @else if (day().hasMore !== 0) {
                Loading...
            }
        </div>
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["calendar.day.td.component.scss"],
})
export class CalendarDayTd {
    readonly day = input.required<CalendarData.DayOfEvents>();

    readonly store = inject(S25CalendarStore);
    readonly dateFormat = s25Resource({ loader: () => UserprefService.getS25Dateformat() });

    readonly reservations = computed(() =>
        (this.day().rsrv ?? [])
            .map((r): CalendarData.Reservation => {
                if (/* is blackout */ !r.event_id && r.rsrv_start_dt && r.rsrv_end_dt) {
                    const startHour = S25Util.date.parse(r.rsrv_start_dt).getHours();
                    const endHour = S25Util.date.parse(r.rsrv_end_dt).getHours();
                    return {
                        ...r,
                        all_day: endHour - startHour === 23 ? 1 : 0,
                    };
                } else {
                    return r;
                }
            })
            // Server will mostly sort the events, but we'll hoist all-day blackouts to the top
            .sort((a, b) => {
                if (/* both are blackouts */ !a.event_id && !b.event_id) {
                    return b.all_day! - a.all_day!;
                }
                return 0;
            }),
    );
    readonly isMonthStart = computed(() => this.day().day == 1 || !!this.day().is_start);
    readonly showCreate = computed(
        () => (!S25Util.isInIframe || S25Const.allowEmbeddedEventCreation) && !!this.day().create_event,
    );

    fullDayModal(): void {
        ModalService.modal("calendar-day", {
            date: S25Util.date.parse(this.day().date),
            expectedTotal: this.day().hasMore || this.reservations().length,
            objId: this.store.calData().obj_id,
            comptype: this.store.comptype(),
            compsubject: this.store.compsubject(),
            includeAdditional: !!this.store.optBean()?.includeAddtl,
            title: "Event List for " + S25Datefilter.transform(this.day().date, this.dateFormat.value()),
        });
    }

    createEvent(): void {
        const itemBean: Record<string, unknown> = {
            startDt: this.day().date,
        };
        if (
            [Item.Ids.Organization, Item.Ids.Location, Item.Ids.Resource].includes(this.store.modelBean()?.itemTypeId)
        ) {
            itemBean.itemId = this.store.modelBean().itemId;
        }
        const itemTypeId = S25Const.itemName2Id[this.store.compsubject()];
        StateService.createEvent(itemBean, itemTypeId);
    }
}
